/**
 * Error handling utilities for API responses
 */

import { tKey } from 'ember-intl';

export interface ApiError {
  detail: string;
  error: string;
}

export interface ApiErrorResponse {
  data?: ApiError;
  status: number;
}

export const LOGIN_CONTEXT = {
  EMAIL_LOGIN: 'email-login',
  CODE_VERIFICATION: 'code-verification',
} as const;

/**
 * Validation error messages
 */
export const VALIDATION_TRANSLATION_KEYS = {
  INVALID_EMAIL: tKey('login.validation.invalid_email'),
  INVALID_CODE: tKey('login.validation.invalid_code'),
  EMAIL_VERIFICATION_FAILED: tKey('login.validation.email_verification_failed'),
} as const;

/**
 * Error context types for different API endpoints
 */
export type ErrorContext = (typeof LOGIN_CONTEXT)[keyof typeof LOGIN_CONTEXT];

/**
 * Valid status codes for error translation keys
 */
export type ErrorStatusCode =
  keyof (typeof ERROR_TRANSLATION_KEYS)[ErrorContext];

/**
 * Translation key mappings for different contexts and status codes
 */
export const ERROR_TRANSLATION_KEYS = {
  [LOGIN_CONTEXT.EMAIL_LOGIN]: {
    400: tKey('login.email.error.400'),
    401: tKey('login.email.error.401'),
    429: tKey('general.error.429'),
    500: tKey('general.error.500'),
    502: tKey('general.error.502'),
    default: tKey('general.error.default'),
  },
  [LOGIN_CONTEXT.CODE_VERIFICATION]: {
    400: tKey('login.code.error.400'),
    401: tKey('login.code.error.401'),
    429: tKey('login.code.error.429'),
    500: tKey('general.error.500'),
    502: tKey('general.error.502'),
    default: tKey('general.error.default'),
  },
} as const;

/**
 * Gets the appropriate translation key for an error
 */
export function getErrorTranslationKey(
  error: unknown,
  context: ErrorContext,
): string {
  if (isApiErrorResponse(error)) {
    const statusCode = error.status as ErrorStatusCode;
    const contextKeys = ERROR_TRANSLATION_KEYS[context];
    const translationKey = contextKeys[statusCode] || contextKeys.default;

    return translationKey;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return ERROR_TRANSLATION_KEYS[context].default;
}

/**
 * Type guard to check if an error is an API error response
 */
function isApiErrorResponse(error: unknown): error is ApiErrorResponse {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  );
}

/**
 * Determines if an error should be reported to Sentry
 * Only reports actual system/API errors, not user validation errors
 */
export function shouldReportToSentry(error: unknown): boolean {
  if (!isApiErrorResponse(error)) {
    // Non-API errors (like network issues) should be reported
    return true;
  }

  // Don't report user errors (4xx client errors)
  if (error.status >= 400 && error.status < 500) {
    // Exception: 429 (rate limiting) should be reported as it's a system concern
    return error.status === 429;
  }

  // Report server errors (5xx) and other unexpected statuses
  return true;
}
